// ProductRevealKids — soft pink + baby blue tablet edition.
// Mirrors ProductReveal.jsx but tuned for the kids edition (baby-shower gift framing).
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { TeddyIcon } from "../TeddyIcon";

const FADE = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } };

export function ProductRevealKids() {
    const { t } = useTranslation();
    const features = [
        { t: t("product_kids.feature_1_title"), b: t("product_kids.feature_1_body") },
        { t: t("product_kids.feature_2_title"), b: t("product_kids.feature_2_body") },
        { t: t("product_kids.feature_3_title"), b: t("product_kids.feature_3_body") },
    ];

    return (
        <section
            className="relative py-32 px-6 bg-gradient-to-b from-white via-[#FBF8F2] to-white overflow-hidden"
            data-testid="product-kids-section"
        >
            <div className="max-w-7xl mx-auto">
                <motion.div {...FADE} className="text-center mb-16">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#A85D62] font-semibold mb-6">
                        {t("product_kids.eyebrow")}
                    </p>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-tight max-w-3xl mx-auto">
                        {t("product_kids.headline_part1")}{" "}
                        <em className="italic text-[#A85D62]">{t("product_kids.headline_part2")}</em><br />
                        {t("product_kids.headline_part3")}
                    </h2>
                </motion.div>

                {/* Side-by-side blue + pink mockups */}
                <motion.div
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.1 }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto"
                >
                    <div className="rounded-[2.5rem] overflow-hidden shadow-rem-card bg-[#EAF2F9]">
                        <img
                            src="/brand/packaging/inbox/blue-box-open-inbox.png"
                            alt="remembr kids edition — light baby-blue gift box, open, with tablet, headphones and plush teddy nestled inside"
                            className="w-full h-auto object-cover"
                            loading="lazy"
                        />
                    </div>
                    <div className="rounded-[2.5rem] overflow-hidden shadow-rem-card bg-[#FBF1F2]">
                        <img
                            src="/brand/packaging/inbox/kids-box-open-inbox.png"
                            alt="remembr kids edition — light baby-pink gift box, open, with tablet, headphones and plush teddy nestled inside"
                            className="w-full h-auto object-cover"
                            loading="lazy"
                        />
                    </div>
                </motion.div>

                <motion.div
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.15 }}
                    className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6"
                >
                    {features.map((f, i) => {
                        // Alternate chip colour to mirror the boy/girl/gift triad.
                        const chipColors = ["#3F6C99", "#A85D62", "#6B7DB8"];
                        const ChipIcon = i === 2 ? ForgetMeNotIcon : TeddyIcon;
                        const color = chipColors[i] || "#A85D62";
                        return (
                            <div
                                key={f.t}
                                className="bg-white/80 backdrop-blur rounded-2xl p-7 border border-[#E5E1D8]/60"
                                data-testid={`product-kids-feature-${i}`}
                            >
                                <div className="flex items-center gap-2 mb-3">
                                    <ChipIcon size={18} color={color} />
                                    <p className="font-sans text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color }}>
                                        {t("product_kids.bundle_includes")}
                                    </p>
                                </div>
                                <h3 className="font-serif text-2xl text-[#2D2B2A] mb-2">{f.t}</h3>
                                <p className="text-[#5C5A59] leading-relaxed font-light text-sm">{f.b}</p>
                            </div>
                        );
                    })}
                </motion.div>

                <motion.p
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.25 }}
                    className="mt-12 text-center text-sm text-[#8A8786] max-w-2xl mx-auto"
                    data-testid="product-kids-shipping-note"
                >
                    {t("product_kids.shipping_note")}
                </motion.p>
            </div>
        </section>
    );
}
